import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { AppShell } from "@/components/AppShell";
import { OrderCard } from "@/components/OrderCard";
import {
  DEMO_ORDERS,
  type BuyOrder,
  type Condition,
  type GradingCo,
  type RawGrade,
  type TCG,
} from "@/lib/marketplace";
import { listOpenBuyOrders } from "@/lib/buy-orders.functions";
import { getHeroExamplePool } from "@/lib/hero-example.functions";
import { getMarketPulseVisibility } from "@/lib/market-pulse.functions";
import { ShieldCheck, Camera, Star, ArrowRight, CheckCircle2, Sparkles, X, Plus } from "lucide-react";
import { HeldFundsInfo } from "@/components/HeldFundsInfo";
import { TrendingRightNow, MarketPulse, WatchlistPanel, BuyOrderStatusSummary } from "@/components/HomeWidgets";
import { PriceInline } from "@/lib/currency";

const browseSearchSchema = z.object({
  q: fallback(z.string(), "").default(""),
  tcg: fallback(z.string().optional(), undefined).optional(),
  cond: fallback(z.enum(["all", "graded", "raw"]).optional(), undefined).optional(),
  co: fallback(z.enum(["PSA", "BGS", "CGC", "any"]).optional(), undefined).optional(),
  gmin: fallback(z.string().optional(), undefined).optional(),
  gmax: fallback(z.string().optional(), undefined).optional(),
  pmin: fallback(z.string().optional(), undefined).optional(),
  pmax: fallback(z.string().optional(), undefined).optional(),
  lang: fallback(z.string().optional(), undefined).optional(),
  est: fallback(z.string().optional(), undefined).optional(),
  sort: fallback(z.enum(["newest", "price_desc", "offers", "fulfilled"]).optional(), undefined).optional(),
});

export const Route = createFileRoute("/")({
  validateSearch: zodValidator(browseSearchSchema),
  head: () => ({
    meta: [
      { title: "Want To Buy — TCG connection platform for collectors" },
      { name: "description", content: "Post what you want. Get connected with sellers who have it. Want To Buy is a connection platform for TCG collectors — a mandatory 2-photo scam filter, PayPal Goods & Services strongly recommended for payment protection." },
    ],
  }),
  component: BrowsePage,
});

const TCG_CHOICES: TCG[] = ["Pokémon", "One Piece", "Magic: The Gathering", "Yu-Gi-Oh", "Lorcana", "Digimon"];

function mapDbTcg(s: string | null): TCG {
  if (!s) return "Pokémon";
  const norm = s.toLowerCase().trim().replace(/[\s-]+/g, "_");
  const slugMap: Record<string, TCG> = {
    pokemon: "Pokémon",
    pokémon: "Pokémon",
    one_piece: "One Piece",
    onepiece: "One Piece",
    magic_the_gathering: "Magic: The Gathering",
    magic: "Magic: The Gathering",
    mtg: "Magic: The Gathering",
    yugioh: "Yu-Gi-Oh",
    yu_gi_oh: "Yu-Gi-Oh",
    lorcana: "Lorcana",
    digimon: "Digimon",
  };
  if (slugMap[norm]) return slugMap[norm];
  const hit = TCG_CHOICES.find((t) => t.toLowerCase() === s.toLowerCase());
  return hit ?? "Pokémon";
}

function parseConditionMin(s: string | null): { condition: Condition; rawGrade?: RawGrade; gradingCo?: GradingCo; minGrade?: number } {
  if (!s) return { condition: "raw" };
  const upper = s.toUpperCase();
  if (["NM", "LP", "MP", "HP", "DMG"].includes(upper)) {
    return { condition: "raw", rawGrade: upper as RawGrade };
  }
  // Try "PSA 9" / "BGS 9.5" / "CGC 10"
  const m = s.match(/^(PSA|BGS|CGC)\s*(\d+(?:\.\d+)?)/i);
  if (m) {
    return { condition: "graded", gradingCo: m[1].toUpperCase() as GradingCo, minGrade: Number(m[2]) };
  }
  return { condition: "raw" };
}

function relTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function seedFromId(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function mapDbOrder(row: any): BuyOrder {
  const cond = parseConditionMin(row.condition_min);
  // Sports orders store the sport name in `sport` and tcg='other'. Surface the
  // sport as the display/filter label so filtering treats it just like a TCG.
  const displayCategory = (row.sport as string | null) || mapDbTcg(row.tcg);
  return {
    id: row.id,
    shortId: String(row.id).slice(0, 5).toUpperCase(),
    buyer: "",
    tcg: displayCategory as TCG,
    cardName: row.card_name || row.title || "Untitled",
    set: "",
    cardNumber: "",
    condition: cond.condition,
    rawGrade: cond.rawGrade,
    gradingCo: cond.gradingCo,
    minGrade: cond.minGrade,
    targetPrice: Math.round((row.max_price_cents ?? 0) / 100),
    offerCount: 0,
    status: "open",
    createdAt: row.created_at ? relTime(row.created_at) : "",
    imageSeed: seedFromId(row.id),
    imageUrl: row.reference_image_url ?? undefined,
    preferredShipping: "Standard Tracked",
    signatureRequired: false,
    cardLanguage: (row.card_language as string | null) ?? null,
  };
}

// Returns the canonical label if the input maps to a known primary TCG,
// otherwise returns the raw label so downstream filtering can attempt an
// exact match (and fall through to an empty state for unsupported games).
function normalizeTcgLoose(input: string | null | undefined): string | null {
  if (!input) return null;
  const raw = String(input).trim();
  if (!raw) return null;
  const s = raw.toLowerCase().replace(/[\s\-:!.]+/g, "_");
  const map: Record<string, TCG> = {
    pokemon: "Pokémon",
    pokémon: "Pokémon",
    one_piece: "One Piece",
    onepiece: "One Piece",
    magic: "Magic: The Gathering",
    mtg: "Magic: The Gathering",
    magic_the_gathering: "Magic: The Gathering",
    yugioh: "Yu-Gi-Oh",
    yu_gi_oh: "Yu-Gi-Oh",
    disney_lorcana: "Lorcana",
    lorcana: "Lorcana",
    digimon: "Digimon",
  };
  if (map[s]) return map[s];
  const hit = TCG_CHOICES.find((t) => t.toLowerCase() === raw.toLowerCase());
  return hit ?? raw;
}

function BrowsePage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const searchQ = search.q ?? "";
  const [showDemoTip, setShowDemoTip] = useState(false);
  const establishedOnly = search.est === "1";

  // tcg param is comma-separated to support multi-select from the Filters panel.
  const tcgParamList = useMemo<string[]>(
    () => (search.tcg ? search.tcg.split(",").map((s: string) => s.trim()).filter(Boolean) : []),
    [search.tcg],
  );
  const activeTcgLabels = useMemo<string[]>(
    () =>
      tcgParamList
        .map((v: string) => normalizeTcgLoose(v))
        .filter((v): v is string => !!v),
    [tcgParamList],
  );
  const singleTcgLabel = tcgParamList.length === 1 ? (tcgParamList[0] ?? null) : null;
  const cond = search.cond ?? "all";
  const co = search.co ?? "any";
  // lang param is comma-separated to support multi-select from the Filters panel.
  // Values: english | japanese | chinese | korean | other | any (any = orders where
  // buyer did not specify a language). Buyer-unrestricted orders always match a
  // named-language filter, since the buyer put no restriction on language.
  const langList = useMemo<string[]>(
    () =>
      search.lang
        ? search.lang
            .split(",")
            .map((s: string) => s.trim().toLowerCase())
            .filter(Boolean)
        : [],
    [search.lang],
  );

  const fetchOrders = useServerFn(listOpenBuyOrders);
  const { data, isLoading, error: queryError } = useQuery({
    queryKey: ["browse", "buy_orders", establishedOnly],
    queryFn: () => fetchOrders({ data: { established_buyers_only: establishedOnly } }),
  });

  const fetchHeroPool = useServerFn(getHeroExamplePool);
  const { data: heroPool } = useQuery({
    queryKey: ["hero-example-pool"],
    queryFn: () => fetchHeroPool(),
    staleTime: 60_000,
  });
  const [heroIdx, setHeroIdx] = useState(0);
  const [heroPaused, setHeroPaused] = useState(false);
  useEffect(() => {
    if (heroPaused || !heroPool || heroPool.length <= 1) return;
    const t = setInterval(() => setHeroIdx((i) => (i + 1) % heroPool.length), 6000);
    return () => clearInterval(t);
  }, [heroPool, heroPaused]);
  const heroExample = heroPool && heroPool.length > 0 ? heroPool[heroIdx % heroPool.length] : undefined;
  const loadError = queryError ? (queryError instanceof Error ? queryError.message : "Failed to load buy orders") : data?.error ?? null;

  const fetchMarketPulseVisibility = useServerFn(getMarketPulseVisibility);
  const { data: marketPulseVisibility } = useQuery({
    queryKey: ["market-pulse-visibility"],
    queryFn: () => fetchMarketPulseVisibility(),
    staleTime: 60_000,
  });

  const liveOrders: BuyOrder[] = useMemo(() => (data?.orders ?? []).map(mapDbOrder), [data]);

  const orders = useMemo(() => {
    const term = searchQ.trim().toLowerCase();
    const gFrom = search.gmin ? Number(search.gmin) : null;
    const gTo = search.gmax ? Number(search.gmax) : null;
    const pFrom = search.pmin ? Number(search.pmin) : null;
    const pTo = search.pmax ? Number(search.pmax) : null;

    const filtered = liveOrders.filter((o) => {
      if (tcgParamList.length > 0 && !activeTcgLabels.includes(o.tcg)) return false;
      if (cond === "graded" && o.condition !== "graded") return false;
      if (cond === "raw" && o.condition !== "raw") return false;
      if (co !== "any" && o.gradingCo !== co) return false;
      if (gFrom != null && (o.minGrade ?? 0) < gFrom) return false;
      if (gTo != null && (o.minGrade ?? 10) > gTo) return false;
      if (pFrom != null && o.targetPrice < pFrom) return false;
      if (pTo != null && o.targetPrice > pTo) return false;
      if (langList.length > 0) {
        const ol = (o.cardLanguage ?? "").toLowerCase();
        // Buyer-unrestricted orders (ol === "") match any named-language filter.
        // Filter token "any" specifically matches buyer-unrestricted orders.
        if (ol !== "" && !langList.includes(ol)) return false;
      }
      if (term) {
        const hay = `${o.cardName} ${o.tcg} ${o.shortId} ${o.set ?? ""}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });

    const sort = search.sort ?? "newest";
    if (sort === "price_desc") {
      filtered.sort((a, b) => b.targetPrice - a.targetPrice);
    } else if (sort === "offers") {
      filtered.sort((a, b) => (b.offerCount ?? 0) - (a.offerCount ?? 0));
    }
    // "newest" is default DB order; "fulfilled" not applicable to open orders.
    return filtered;
  }, [liveOrders, searchQ, tcgParamList, activeTcgLabels, cond, co, langList, search.gmin, search.gmax, search.pmin, search.pmax, search.sort]);

  useEffect(() => {
    try {
      if (!localStorage.getItem("w2b-demo-tip-seen")) {
        setShowDemoTip(true);
      }
    } catch {}
  }, []);

  function dismissDemoTip() {
    setShowDemoTip(false);
    try { localStorage.setItem("w2b-demo-tip-seen", "1"); } catch {}
  }

  const tcgFilterActive = tcgParamList.length > 0;

  return (
    <AppShell>
      {/* HERO — only on unfiltered landing view */}
      {!tcgFilterActive && (
      <section className="mb-6 panel relative overflow-hidden p-5 sm:p-10">

        <div className="pointer-events-none absolute -right-20 -top-20 h-80 w-80 rounded-full bg-primary/25 blur-3xl" />
        <div className="pointer-events-none absolute -left-16 bottom-0 h-64 w-64 rounded-full bg-primary/15 blur-3xl" />
        <div className="relative grid gap-10 lg:grid-cols-[1.05fr_1fr] lg:items-stretch">
          <div>
            <span className="status-pill bg-primary/15 text-primary border border-primary/30">Connection platform</span>
            <h1 className="mt-3 font-display text-3xl font-black tracking-tight break-words sm:text-5xl lg:text-6xl">
              <span className="block text-foreground">Post what you want.</span>
              <span className="mt-1 block text-primary">Get connected with sellers who have it.</span>
            </h1>
            <p className="mt-2 font-display text-xs font-bold uppercase tracking-[0.25em] text-primary/90">
              For Collectors, By Collectors
            </p>
            <p className="mt-4 max-w-xl text-sm text-muted-foreground sm:text-base">
              Want To Buy connects TCG buyers with sellers who have the cards they're looking for. Sellers must pass a mandatory 2-photo scam filter before you're connected. From there, payment and shipping are between you and the seller — <a href="/payment-safety" className="text-primary hover:underline">PayPal Goods &amp; Services strongly recommended</a>.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/create-order" className="btn-primary inline-flex items-center gap-2 px-5 py-3 text-sm">
                Post a buy order <ArrowRight className="h-4 w-4" />
              </Link>
              <a href="#open-orders" className="btn-ghost inline-flex items-center gap-2 px-5 py-3 text-sm">
                Browse open orders
              </a>
            </div>
            <div className="mt-6 flex flex-wrap gap-4 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-success" /> <HeldFundsInfo label="Peer-to-peer payments" /></span>
              <span className="inline-flex items-center gap-1.5"><Camera className="h-4 w-4 text-primary" /> Mandatory scam-filter photos</span>
              <span className="inline-flex items-center gap-1.5"><Star className="h-4 w-4 text-warning" /> Rated sellers</span>
            </div>
          </div>

          {/* Rotating example card — pulls from live orders gaining traction */}
          <div className="relative w-full">
            <div
              className="panel relative flex h-full flex-col overflow-hidden p-5 shadow-2xl"
              onMouseEnter={() => setHeroPaused(true)}
              onMouseLeave={() => setHeroPaused(false)}
            >
              <div className="flex items-center justify-between">
                <span className="status-pill bg-muted text-muted-foreground border border-border">
                  {heroExample?.source === "live" ? "Gaining traction" : "Example"}
                </span>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">How offers look</span>
              </div>
              <div className="mt-2 flex flex-1 flex-col items-start gap-4 sm:flex-row sm:items-center sm:gap-5">
                {heroExample?.imageUrl ? (
                  <img
                    src={heroExample.imageUrl}
                    alt={heroExample.cardName}
                    className="mx-auto aspect-[5/7] w-40 shrink-0 rounded-md border border-border object-cover sm:mx-0 sm:w-56 lg:w-64"
                  />
                ) : null}
                <div className="w-full min-w-0">
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {heroExample?.tcg ?? "Pokémon"}
                  </div>
                  <div className="font-display text-2xl font-bold leading-tight break-words sm:text-3xl">
                    {heroExample?.cardName ?? "Charizard PSA 9"}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {(heroExample?.setName ?? "Base Set")}
                    {(heroExample?.cardNumber ?? "4/102") ? ` #${heroExample?.cardNumber ?? "4/102"}` : ""}
                    {" · Target "}
                    <PriceInline usd={(heroExample?.targetPriceCents ?? 420000) / 100} />
                  </div>
                  {heroExample?.source === "live" ? (
                    <Link
                      to="/orders/$id"
                      params={{ id: heroExample.id }}
                      className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
                    >
                      View order <ArrowRight className="h-4 w-4" />
                    </Link>
                  ) : null}
                </div>
              </div>
              {heroPool && heroPool.length > 1 ? (
                <div className="mt-auto flex items-center justify-center gap-1.5 pt-3">
                  {heroPool.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      aria-label={`Show order ${i + 1}`}
                      onClick={() => setHeroIdx(i)}
                      className={`h-1.5 rounded-full transition-all ${
                        i === heroIdx % heroPool.length ? "w-6 bg-primary" : "w-1.5 bg-muted-foreground/40"
                      }`}
                    />
                  ))}
                </div>
              ) : null}
            </div>
            <div className="pointer-events-none absolute -inset-4 -z-10 rounded-3xl bg-primary/20 blur-2xl" />
          </div>
        </div>
      </section>
      )}





      {!tcgFilterActive && (
        <>
          <BuyOrderStatusSummary />
          <div className="mb-10 grid gap-6 lg:grid-cols-[65fr_35fr]">
            <TrendingRightNow />
            <WatchlistPanel />
          </div>
          {marketPulseVisibility?.visible && <MarketPulse />}

          <section className="mb-14">
            <div className="text-center">
              <h2 className="font-display text-3xl font-bold sm:text-4xl">How the connection works</h2>
              <p className="mt-2 text-sm text-muted-foreground sm:text-base">Four steps from want list to connected.</p>
            </div>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { n: "01", t: "Post a buy order", d: "Describe the card, condition, and the price you're willing to pay." },
                { n: "02", t: "Receive offers", d: "Sellers who have the card submit offers. Compare price, rating, and notes." },
                { n: "03", t: "Mandatory scam-filter photos", d: "After you accept, the seller submits 2 timestamped photos (front + back with handwritten note) proving they physically hold the card." },
                { n: "04", t: "Get connected & transact", d: "The buyer reviews the 2 mandatory photos and — if satisfied — approves. (Buyers can informally ask sellers for extra photos via message, but only the original 2 are required to proceed.) Once approved, the message thread opens and the two parties arrange payment and shipping directly — PayPal Goods & Services is strongly recommended because PayPal provides independent buyer protection." },
              ].map(step => (

                <div key={step.n} className="panel p-5 transition hover:border-primary/50">
                  <div className="font-display text-3xl font-black text-primary">{step.n}</div>
                  <div className="mt-2 font-display text-lg font-bold text-foreground">{step.t}</div>
                  <p className="mt-1.5 text-sm text-muted-foreground">{step.d}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-center text-xs text-muted-foreground max-w-2xl mx-auto">
              Want To Buy is a connection platform. It does not process payments, hold funds, or mediate disputes. All transactions are between the buyer and the seller. See <a href="/payment-safety" className="text-primary hover:underline">Payment Safety</a> for how PayPal protection works and how to use your message thread as evidence.
            </p>
          </section>
        </>
      )}


      <div id="open-orders">


        <div className="min-w-0 flex-1">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-display text-xl font-bold">
              {searchQ ? <>Results for <span className="text-primary">"{searchQ}"</span></> : "Open orders"}
            </h2>
            <div className="flex flex-wrap items-center gap-3">
              <label
                className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-border bg-background/40 px-3 py-1.5 text-xs"
                title="Established Buyers have completed 3 or more purchases with a 90%+ rating from sellers — a signal of reliable payment and communication."
              >
                <input
                  type="checkbox"
                  checked={establishedOnly}
                  onChange={(e) => {
                    const { est: _drop, ...rest } = search;
                    navigate({
                      to: "/",
                      search: e.target.checked ? { ...rest, est: "1" } : rest,
                    });
                  }}
                  className="h-3.5 w-3.5"
                />
                Established Buyers
              </label>
              <Link to="/verified-stores" className="text-xs text-primary underline">
                Browse Verified Stores →
              </Link>
              <div className="text-sm text-muted-foreground">
                {isLoading ? "Loading…" : `${orders.length} ${searchQ ? "results" : "open orders"}`}
              </div>
            </div>
          </div>

          {/* Demo orders pinned at top — only on unfiltered landing view */}
          {!tcgFilterActive && (
          <div className="mb-6 relative">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="font-display text-sm font-bold uppercase tracking-wider text-primary flex items-center gap-2">
                <Sparkles className="h-4 w-4" /> Try a demo order
              </h3>
              <span className="text-xs text-muted-foreground">Interactive walkthrough — no payment required</span>
            </div>
            {showDemoTip && (
              <div className="mb-3 flex items-start justify-between gap-3 rounded-md border border-primary/40 bg-primary/10 p-3 text-sm">
                <div className="flex gap-2">
                  <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <span><span className="font-semibold">New here?</span> Try a demo order to see how Want To Buy works end-to-end.</span>
                </div>
                <button onClick={dismissDemoTip} aria-label="Dismiss tip" className="shrink-0 text-muted-foreground hover:text-foreground">
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              {DEMO_ORDERS.map((o) => <OrderCard key={o.id} order={o} />)}
            </div>
          </div>
          )}


          {isLoading ? (
            <div className="rounded-md border border-dashed border-border bg-background/40 p-10 text-center text-sm text-muted-foreground">
              Loading open orders…
            </div>
          ) : loadError ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 p-10 text-center text-sm text-destructive">
              Couldn't load open buy orders: {loadError}
            </div>
          ) : orders.length === 0 ? (
            singleTcgLabel ? (
              <div className="rounded-md border border-dashed border-border bg-background/40 p-10 text-center">
                <p className="text-sm text-muted-foreground">
                  No open buy orders for <span className="font-semibold text-foreground">{singleTcgLabel}</span> yet — be the first to post one.
                </p>
                <Link
                  to="/create-order"
                  search={{ tcg: singleTcgLabel }}
                  className="btn-primary mt-4 inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold"
                >
                  <Plus className="h-3.5 w-3.5" /> Post a Buy Order
                </Link>
              </div>
            ) : (
              <div className="rounded-md border border-dashed border-border bg-background/40 p-10 text-center text-sm text-muted-foreground">
                {searchQ ? `No orders match "${searchQ}".` : "No open buy orders yet. Be the first to post one."}
              </div>
            )
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              {orders.map((o) => <OrderCard key={o.id} order={o} />)}
            </div>
          )}
        </div>
      </div>

      {!(singleTcgLabel && orders.length === 0 && !isLoading && !loadError) && (
        <section
          className="relative mt-16 overflow-hidden rounded-2xl p-10 text-center sm:p-14"
          style={{ backgroundColor: "#16213E" }}
        >
          <div className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-primary/30 blur-3xl" />
          <div className="pointer-events-none absolute -left-16 -bottom-16 h-72 w-72 rounded-full bg-primary/15 blur-3xl" />
          <div className="relative">
            <h2 className="font-display text-3xl font-black text-white sm:text-4xl">Can't find what you're looking for?</h2>
            <p className="mx-auto mt-3 max-w-xl text-sm text-white/75 sm:text-base">
              Post a buy order and let sellers come to you.
            </p>
            <Link
              to="/create-order"
              className="btn-primary mt-6 inline-flex items-center gap-2 px-6 py-3 text-sm font-bold"
            >
              <Plus className="h-4 w-4" /> Post Buy Order
            </Link>
            <div className="mt-4 flex items-center justify-center gap-2 text-xs text-white/60">
              <CheckCircle2 className="h-3.5 w-3.5" /> Timestamped-photo verification · PayPal Goods & Services · Tracked shipping
            </div>
          </div>
        </section>
      )}
    </AppShell>
  );
}
